import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Globe2,
  ArrowUpRight,
  FileDown,
  Linkedin,
  Phone,
  Play,
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo/BrandLogo";

export default function BrandDetail() {
  const { company } = useParams();
  const location = useLocation();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBrand = () => {
      setLoading(true);

      // Get sponsor data from navigation state
      const sponsorData = location.state?.sponsorData;

      if (sponsorData) {
        // Check if data is already normalized (from EventDetail) or raw (from tables API)
        const isNormalized = sponsorData.company && !sponsorData.title;

        const normalized = isNormalized
          ? {
              // Already normalized from EventDetail - use as is
              company: sponsorData.company || decodeURIComponent(company),
              value_prop: sponsorData.value_prop || "",
              overview: sponsorData.overview || "",
              contact_email: sponsorData.contact_email || "",
              phone: sponsorData.phone || "",
              website: sponsorData.website || "",
              linkedin: sponsorData.linkedin || "",
              presentation_url: sponsorData.presentation_url || "",
              pdf_url: sponsorData.pdf_url || "",
              pdf_caption:
                sponsorData.pdf_caption || "Download one-pager (PDF)",
              video_url: sponsorData.video_url || "",
              logo: sponsorData.logo || "",
              bgColor: sponsorData.bgColor || "#1E293B",
              chapterName: sponsorData.chapterName,
              visibility: sponsorData.visibility,
              _id: sponsorData.id || sponsorData._id,
            }
          : {
              // Raw data from tables API - normalize it
              company: sponsorData.title || decodeURIComponent(company),
              value_prop: "",
              overview: sponsorData.descp || "",
              contact_email: sponsorData.email || "",
              phone: sponsorData.phone || "",
              website: sponsorData.website || "",
              linkedin: sponsorData.linkedin || "",
              presentation_url: sponsorData.presentationUrl || "",
              pdf_url: sponsorData.pdfUrl || "",
              pdf_caption: sponsorData.pdfCaption || "Download one-pager (PDF)",
              video_url: sponsorData.introVideo || "",
              logo: sponsorData.image || "",
              bgColor: sponsorData.bgcColor || "#1E293B",
              chapterName: sponsorData.chapterName,
              visibility: sponsorData.visibility,
              _id: sponsorData._id,
            };

        setBrand(normalized);
      } else {
        // No sponsor data passed - show not found
        setBrand(false);
      }

      setLoading(false);
    };

    loadBrand();
  }, [company, location.state]);
  if (loading) {
    return (
      <div
        className="container-x py-32 text-white/55"
        data-testid="brand-loading"
      >
        Loading sponsor details…
      </div>
    );
  }

  if (brand === false) {
    return (
      <div className="container-x py-32" data-testid="brand-not-found">
        <Link
          to={-1}
          className="overline text-white/55 hover:text-white inline-flex items-center gap-2 mb-8"
        >
          <ArrowLeft size={12} /> Back
        </Link>
        <h1 className="font-display text-4xl mb-4">Sponsor not found</h1>
        <p className="text-white/70">
          We couldn't find sponsor information for "
          {decodeURIComponent(company || "")}".
        </p>
      </div>
    );
  }

  if (!brand) return null;

  // Helper to convert YouTube/Vimeo URLs to embed URLs
  const getEmbedUrl = (url) => {
    if (!url) return null;

    // If already an embed URL, return as is
    if (url.includes("/embed/")) return url;

    // YouTube
    const ytMatch = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/,
    );
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;

    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

    // Direct video file - return as is
    return url;
  };

  const embedUrl = getEmbedUrl(brand.video_url);
  const isDirectVideo =
    embedUrl &&
    !embedUrl.includes("youtube.com") &&
    !embedUrl.includes("vimeo.com");

  return (
    <div data-testid="brand-detail-page" className="min-h-screen">
      <section
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
        className="border-b border-white/[0.06]"
      >
        <div className="container-x py-16 md:py-24">
          <Link
            to={-1}
            className="overline text-white/55 hover:text-white inline-flex items-center gap-2 mb-8"
          >
            <ArrowLeft size={12} /> Back to lobby
          </Link>
          <div className="grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-8">
              <p className="overline mb-5">Sponsor profile</p>
              <div className="flex items-center gap-5 mb-6">
                {brand.logo ? (
                  <div
                    className="w-[88px] h-[88px] rounded-xl flex items-center justify-center overflow-hidden border border-white/10"
                    style={{ backgroundColor: brand.bgColor || "#1E293B" }}
                  >
                    <img
                      src={brand.logo}
                      alt={brand.company}
                      className="max-w-[80%] max-h-[80%] object-contain"
                    />
                  </div>
                ) : (
                  <BrandLogo company={brand.company} size={88} />
                )}
                <h1 className="font-display font-light text-5xl md:text-6xl tracking-tighter leading-[0.95]">
                  {brand.company}
                </h1>
              </div>
              {brand.value_prop && (
                <p
                  className="font-display italic text-2xl md:text-3xl text-[#C6A87C] tracking-tight mb-8 max-w-2xl"
                  data-testid="brand-value-prop"
                >
                  {brand.value_prop}
                </p>
              )}
              {brand.overview && (
                <div
                  className="text-white/75 leading-relaxed max-w-2xl prose prose-invert"
                  data-testid="brand-overview"
                  dangerouslySetInnerHTML={{ __html: brand.overview }}
                />
              )}

              {brand.chapterName && (
                <div className="mt-8 flex items-center gap-2 text-sm text-white/60">
                  <Globe2 size={14} className="text-[#C6A87C]" />
                  <span>Chapter: {brand.chapterName}</span>
                  {brand.visibility && (
                    <span className="ml-2 px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-xs">
                      {brand.visibility}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="md:col-span-4">
              <div
                className="glass rounded-2xl p-6 space-y-4"
                data-testid="brand-contact-card"
              >
                <p className="overline mb-1">Get in touch</p>
                {brand.contact_email && (
                  <a
                    href={`mailto:${brand.contact_email}`}
                    className="flex items-center gap-3 text-sm hover:text-[#C6A87C] transition-colors"
                    data-testid="brand-contact-email"
                  >
                    <Mail size={14} className="text-[#C6A87C] shrink-0" />
                    <span className="break-all">{brand.contact_email}</span>
                  </a>
                )}
                {brand.phone && (
                  <a
                    href={`tel:${brand.phone}`}
                    className="flex items-center gap-3 text-sm hover:text-[#C6A87C] transition-colors"
                    data-testid="brand-phone"
                  >
                    <Phone size={14} className="text-[#C6A87C] shrink-0" />
                    {brand.phone}
                  </a>
                )}
                {brand.website && (
                  <a
                    href={brand.website}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 text-sm hover:text-[#C6A87C] transition-colors"
                    data-testid="brand-website"
                  >
                    <Globe2 size={14} className="text-[#C6A87C] shrink-0" />
                    Visit website <ArrowUpRight size={12} />
                  </a>
                )}
                {brand.linkedin && (
                  <a
                    href={brand.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 text-sm hover:text-[#C6A87C] transition-colors"
                    data-testid="brand-linkedin"
                  >
                    <Linkedin size={14} className="text-[#C6A87C] shrink-0" />
                    LinkedIn <ArrowUpRight size={12} />
                  </a>
                )}

                {(brand.presentation_url || brand.pdf_url) && (
                  <div className="pt-4 border-t border-white/10 space-y-2">
                    {brand.presentation_url && (
                      <a
                        href={brand.presentation_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full border border-[#C6A87C]/45 text-[#C6A87C] hover:bg-[#C6A87C]/10 transition-colors font-medium w-full justify-center"
                        data-testid="brand-presentation"
                      >
                        <FileDown size={13} /> View presentation deck
                      </a>
                    )}
                    {brand.pdf_url && (
                      <a
                        href={brand.pdf_url}
                        target="_blank"
                        rel="noreferrer"
                        download
                        className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full bg-[#C6A87C] text-[#061412] hover:bg-[#D9BC93] transition-colors font-medium w-full justify-center"
                        data-testid="brand-pdf"
                      >
                        <FileDown size={13} />
                        {brand.pdf_caption || "Download one-pager"}
                      </a>
                    )}
                  </div>
                )}

                {!brand.contact_email &&
                  !brand.phone &&
                  !brand.website &&
                  !brand.linkedin &&
                  !brand.pdf_url &&
                  !brand.presentation_url && (
                    <p className="text-xs text-white/55">
                      No public contact details available yet.
                    </p>
                  )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {embedUrl && (
        <section className="section">
          <div className="container-x">
            <p className="overline mb-5 flex items-center gap-2">
              <Play size={12} className="text-[#C6A87C]" />
              Introduction video
            </p>
            <div
              className="aspect-video rounded-2xl overflow-hidden glass"
              data-testid="brand-video"
            >
              {isDirectVideo ? (
                <video
                  src={embedUrl}
                  controls
                  className="w-full h-full"
                  title={`${brand.company} introduction`}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <iframe
                  src={embedUrl}
                  title={`${brand.company} introduction`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
